import { Box, Divider, Button, HStack, IconButton } from '@chakra-ui/react';

import { SFDownloadOutlined, SFListBulletIndentIcon, SFSquareOnSquareIcon } from '@assets/icons';
import { useMeetingManagementActions, useCopyToClipboard } from '@util/hooks';
import { triggerVTTDownload } from '@util/helpers';
import { useBotAsVTT } from '@services/query-services/bots.query-service';
import { HIDE_SCROLLBAR } from '@styles/css-configs';
import { useUserStore } from '@store/app.store';

import StyledModal from '@components/general/modals/styled-modal.component';
import Transcripts from '@components/meeting/transcripts.component';
import SectionHeader from '@components/meeting/section-header.component';

export default function TranscriptModal({ disclosure, meeting, bot, ...styledModalProps }) {
  const user = useUserStore((state) => state?.user);
  const isGuest = user?.isGuest;
  const copyToClipboard = useCopyToClipboard();
  const { pauseResume } = useMeetingManagementActions(meeting, bot);
  const {
    data: initialVTT,
    refetch: refetchVTT,
    isFetching: isFetchingVTT
  } = useBotAsVTT(bot?.id, {
    enabled: false,
    staleTime: Infinity
  });

  return (
    <StyledModal
      title='Transcript'
      disclosure={disclosure}
      modalProps={{ scrollBehavior: 'inside' }}
      modalContentProps={{ maxH: '80vh', minWidth: { base: '95%', sm: '60%' } }}
      modalBodyProps={{
        overflowy: 'scroll',
        sx: HIDE_SCROLLBAR
      }}
      customHeader={
        <>
          <Box px={1}>
            <SectionHeader
              headline='Transcript'
              meeting={meeting}
              icon={SFListBulletIndentIcon}
              inModal
            />
          </Box>
          <Divider />
        </>
      }
      customFooter={
        !isGuest && (
          <HStack
            position='absolute'
            bottom={4}
            right={4}
            zIndex='10'
          >
            <IconButton
              data-testid='download-transcript-icon-button'
              variant='reskinSecondary'
              icon={<SFDownloadOutlined />}
              onClick={async () => {
                let vtt = initialVTT;
                if (!vtt) {
                  const { data: fetchedVtt } = await refetchVTT();
                  if (!fetchedVtt) {
                    return;
                  }
                  vtt = fetchedVtt;
                }
                triggerVTTDownload(vtt);
              }}
            />
            <Button
              isLoading={isFetchingVTT}
              leftIcon={
                <SFSquareOnSquareIcon
                  boxSize={5}
                  color='brandPrimary.onPrimary'
                />
              }
              variant='reskinPrimary'
              onClick={async () => {
                let vtt = initialVTT;
                if (!vtt) {
                  const { data: fetchedVtt } = await refetchVTT();
                  if (!fetchedVtt) {
                    return;
                  }
                  vtt = fetchedVtt;
                }
                copyToClipboard(vtt);
              }}
            >
              Copy Transcript
            </Button>
          </HStack>
        )
      }
      {...styledModalProps}
    >
      <Transcripts
        bot={bot}
        pauseResume={pauseResume}
        disclosure={disclosure}
      />
    </StyledModal>
  );
}
